"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Row = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const mobx_react_1 = require("mobx-react");
const react_1 = require("react");
const cell_1 = require("./cell");
const KeptGroupRow_1 = require("./KeptGroupRow");
const RowState_1 = require("../utils/RowState");
const sortRows_1 = require("../utils/sortRows");
const TableState_1 = require("../utils/TableState");
const utils_1 = require("../utils/utils");
const Css_1 = require("../../../Css");
const utils_2 = require("../../../utils");
const use_debounce_1 = require("use-debounce");
// We extract Row to its own mini-component primarily so we can React.memo'ize it.
function RowImpl(props) {
    var _a, _b, _c;
    const { as, rs, style, rowStyles, columnSizes, getCount, cellHighlight, omitRowHover, hasExpandableHeader, onDragStart, onDragEnd, onDrop, onDragEnter, onDragOver, ...others } = props;
    const { tableState } = (0, react_1.useContext)(TableState_1.TableStateContext);
    // We're wrapped in observer, so can access these without useComputeds
    const { api, visibleColumns: columns } = tableState;
    const { row, api: rowApi, isActive, isKept: isKeptRow, isLastKeptRow, level } = rs;
    // We treat the "header" and "totals" kind as special for "good defaults" styling
    const isHeader = row.kind === utils_1.HEADER;
    const isTotals = row.kind === utils_1.TOTALS;
    const isExpandableHeader = row.kind === utils_1.EXPANDABLE_HEADER;
    const isKeptGroupRow = row.kind === utils_1.KEPT_GROUP;
    const rowStyle = rowStyles === null || rowStyles === void 0 ? void 0 : rowStyles[row.kind];
    const RowTag = as === "table" ? "tr" : "div";
    const sortOn = (_a = tableState.sortConfig) === null || _a === void 0 ? void 0 : _a.on;
    const revealOnRowHoverClass = "revealOnRowHover";
    const showRowHoverColor = !utils_1.reservedRowKinds.includes(row.kind) && !omitRowHover && style.rowHoverColor !== "none";
    const rowStyleCellCss = (0, utils_1.maybeApplyFunction)(row, rowStyle === null || rowStyle === void 0 ? void 0 : rowStyle.cellCss);
    const levelIndent = style.levels && ((_b = style.levels[level]) === null || _b === void 0 ? void 0 : _b.rowIndent);
    const containerCss = {
        ...Css_1.Css.add("transition", "padding 0.25s ease-in-out").$,
        ...(rs.isDraggedOver === RowState_1.DraggedOver.Above && Css_1.Css.ptPx(25).$),
        ...(rs.isDraggedOver === RowState_1.DraggedOver.Below && Css_1.Css.pbPx(25).$),
    };
    const rowCss = {
        ...(!utils_1.reservedRowKinds.includes(row.kind) && style.nonHeaderRowCss),
        // Optionally include the row hover styles, by default they should be turned on.
        ...(showRowHoverColor && {
            // Even though backgroundColor is set on the cellCss, the hover target is the row.
            "&:hover > *": Css_1.Css.bgColor((_c = style.rowHoverColor) !== null && _c !== void 0 ? _c : Css_1.Palette.Blue100).$,
        }),
        ...(!utils_1.reservedRowKinds.includes(row.kind) &&
            style.nonHeaderRowHoverCss && {
            // Need to spread this to make TS happy.
            ":hover": { ...style.nonHeaderRowHoverCss },
        }),
        ...(levelIndent && Css_1.Css.mlPx(levelIndent).$),
        // For virtual tables use `display: flex` to keep all cells on the same row. For each cell in the row use `fn` to ensure they stay their defined widths
        ...(as === "table" ? {} : Css_1.Css.relative.df.fg1.fs1.addIn("&>*", Css_1.Css.fn.$).$),
        // Apply `cursorPointer` to the row if it has a link or `onClick` value.
        ...(((rowStyle === null || rowStyle === void 0 ? void 0 : rowStyle.rowLink) || (rowStyle === null || rowStyle === void 0 ? void 0 : rowStyle.onClick)) && { "&:hover": Css_1.Css.cursorPointer.$ }),
        ...(0, utils_1.maybeApplyFunction)(row, rowStyle === null || rowStyle === void 0 ? void 0 : rowStyle.rowCss),
        ...{
            [` > .${revealOnRowHoverClass} > *`]: Css_1.Css.vh.$,
            [`:hover > .${revealOnRowHoverClass} > *`]: Css_1.Css.vh.$,
        },
        ...(isLastKeptRow && Css_1.Css.addIn("&>*", style.keptLastRowCss).$),
    };
    let currentColspan = 1;
    // Keep a running count of how many expanded columns are being shown.
    let currentExpandedColumnCount = 0;
    let foundFirstContentColumn = false;
    let minStickyLeftOffset = 0;
    let expandColumnHidden = false;
    // used to render the whole row when dragging with the handle
    const ref = (0, react_1.useRef)(null);
    // debounce drag over callback to avoid excessive re-renders
    const dragOverCallback = (0, react_1.useCallback)((row, evt) => onDragOver === null || onDragOver === void 0 ? void 0 : onDragOver(row, evt), [onDragOver]);
    // when the event is not called, we still need to call preventDefault
    const onDragOverDebounced = (0, use_debounce_1.useDebouncedCallback)(dragOverCallback, 100);
    const RowContent = () => ((0, jsx_runtime_1.jsx)(RowTag, { css: rowCss, ...others, "data-gridrow": true, ...getCount(row.id), ref: ref, children: isKeptGroupRow ? ((0, jsx_runtime_1.jsx)(KeptGroupRow_1.KeptGroupRow, { as: as, style: style, columnSizes: columnSizes, row: row, colSpan: columns.length })) : (columns.map((column, columnIndex) => {
            var _a, _b, _c, _d;
            // If the expandable column was hidden, then we need to look at the previous column to format the `expandHeader` and 'header' kinds correctly.
            const maybeExpandedColumn = expandColumnHidden ? columns[columnIndex - 1] : column;
            // Figure out if this column should be considered 'expanded' or not. If the column is hidden on expand, then we need to look at the previous column to see if it's expanded.
            const isExpanded = tableState.isExpandedColumn(maybeExpandedColumn.id);
            // If the column is hidden on expand, we don't want to render it. We'll flag that it was hidden, so on the next column we can render this column's "expandHeader" property.
            if (column.hideOnExpand && isExpanded) {
                expandColumnHidden = true;
                return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, {});
            }
            // Need to keep track of the expanded columns so we can add borders as expected for the header rows
            const numExpandedColumns = isExpanded
                ? tableState.numberOfExpandedChildren(maybeExpandedColumn.id)
                    ? // Subtract 1 if the column is hidden on expand, since we're not rendering it.
                        tableState.numberOfExpandedChildren(maybeExpandedColumn.id) - (maybeExpandedColumn.hideOnExpand ? 1 : 0)
                    : 0
                : 0;
            // If we're rendering the Expandable Header row, then we might need to render the previous column's `expandHeader` property in the case where the column is hidden on expand.
            column = isExpandableHeader ? maybeExpandedColumn : column;
            const { wrapAction = true, isAction = false } = column;
            const isFirstContentColumn = !isAction && !foundFirstContentColumn;
            const applyFirstContentColumnStyles = !isHeader && isFirstContentColumn;
            foundFirstContentColumn || (foundFirstContentColumn = applyFirstContentColumnStyles);
            // When using the variation of the table with an EXPANDABLE_HEADER, then our HEADER and TOTAL rows have special border styling
            // Keep track of the when we get to the last expanded column so we can apply this styling properly.
            if (hasExpandableHeader && (isHeader || isTotals)) {
                // When the value of `currentExpandedColumnCount` is 0, then we have started over.
                // If the current column `isExpanded`, then store the number of expandable columns.
                if (currentExpandedColumnCount === 0 && isExpanded) {
                    currentExpandedColumnCount = numExpandedColumns;
                }
                else if (currentExpandedColumnCount > 0) {
                    // If value is great than 0, then decrement. Once the value equals 0, then the special styling will be applied below.
                    currentExpandedColumnCount -= 1;
                }
            }
            // Reset the expandColumnHidden flag once done with logic based upon it.
            expandColumnHidden = false;
            // Decrement colspan count and skip if greater than 1.
            if (currentColspan > 1) {
                currentColspan -= 1;
                return null;
            }
            const maybeContent = (0, utils_1.applyRowFn)(column, row, rowApi, level, isExpanded, {
                rowRenderRef: ref,
                onDragStart,
                onDragEnd,
                onDrop,
                onDragEnter,
                onDragOver: onDragOverDebounced,
            });
            // Only use the `numExpandedColumns` as the `colspan` when rendering the "Expandable Header"
            currentColspan =
                (0, utils_1.isGridCellContent)(maybeContent) && typeof maybeContent.colspan === "number"
                    ? maybeContent.colspan
                    : isExpandableHeader
                        ? numExpandedColumns + 1
                        : 1;
            const revealOnRowHover = (0, utils_1.isGridCellContent)(maybeContent) ? maybeContent.revealOnRowHover : false;
            const canSortColumn = (sortOn === "client" && column.clientSideSort !== false) ||
                (sortOn === "server" && !!column.serverSideSortKey);
            const alignment = (0, utils_1.getAlignment)(column, maybeContent);
            const justificationCss = (0, utils_1.getJustification)(column, maybeContent, as, alignment);
            const isExpandable = (0, utils_2.isFunction)(column.expandColumns) ||
                (column.expandColumns && column.expandColumns.length > 0) ||
                column.expandedWidth !== undefined;
            const content = (0, utils_1.toContent)(maybeContent, isHeader, canSortColumn, sortOn === "client", style, as, alignment, column, isExpandableHeader, isExpandable, minStickyLeftOffset, isKeptRow);
            (0, sortRows_1.ensureClientSideSortValueIsSortable)(sortOn, isHeader || isTotals || isExpandableHeader, column, columnIndex, maybeContent);
            const maybeSticky = (_a = (((0, utils_1.isGridCellContent)(maybeContent) && maybeContent.sticky) || column.sticky)) !== null && _a !== void 0 ? _a : undefined;
            const maybeStickyColumnStyles = maybeSticky && columnSizes
                ? {
                    ...Css_1.Css.sticky.z(utils_1.zIndices.stickyColumns).bgWhite.$,
                    ...(maybeSticky === "left"
                        ? Css_1.Css.left(columnIndex === 0 ? 0 : `calc(${columnSizes.slice(0, columnIndex).join(" + ")})`).$
                        : {}),
                    ...(maybeSticky === "right"
                        ? Css_1.Css.right(columnIndex + 1 === columnSizes.length
                            ? 0
                            : `calc(${columnSizes.slice(columnIndex + 1 - columnSizes.length).join(" + ")})`).$
                        : {}),
                }
                : {};
            // This relies on our column sizes being defined in pixel values, which is currently true as we calculate to pixel values in the `useSetupColumnSizes` hook
            minStickyLeftOffset += maybeSticky === "left" ? parseInt(columnSizes[columnIndex].replace("px", ""), 10) : 0;
            const cellId = `${row.kind}_${row.id}_${column.id}`;
            const applyCellHighlight = cellHighlight && !!column.id && !isHeader && !isTotals;
            const isCellActive = tableState.activeCellId === cellId;
            // Note that it seems expensive to calc a per-cell class name/CSS-in-JS output,
            // vs. setting global/table-wide CSS like `style.cellCss` on the root grid div with
            // a few descendent selectors. However, that approach means the root grid-applied
            // CSS has a high-specificity and so its harder for per-page/per-cell business logic
            // to override it. So, we just calc the combined table-wide+per-cell-overridden CSS here,
            // in a very CSS-in-JS idiomatic manner.
            //
            // In practice we've not seen any performance issues with this from our "large but
            // not Google spreadsheets" tables.
            const cellCss = {
                // Adding `display: flex` so we can align content within the cells, unless it is displayed as a `table`, then use `table-cell`.
                ...Css_1.Css.df.if(as === "table").dtc.$,
                // Apply sticky column/cell styles
                ...maybeStickyColumnStyles,
                // Apply any static/all-cell styling
                ...style.cellCss,
                // Then override with first/last cell styling
                ...(0, utils_1.getFirstOrLastCellCss)(style, columnIndex, columns),
                // Then override with per-cell/per-row justification
                ...justificationCss,
                // Then apply any header-specific override
                ...(isHeader && style.headerCellCss),
                // Then apply any totals-specific override
                ...(isTotals && style.totalsCellCss),
                ...(isTotals && hasExpandableHeader && Css_1.Css.boxShadow(`inset 0 -1px 0 ${Css_1.Palette.Gray200}`).$),
                // Then apply any expandable header specific override
                ...(isExpandableHeader && style.expandableHeaderCss),
                // Conditionally apply the right border styling for the header or totals row when using expandable tables
                // Only apply if not the last column in the table AND when this column is the last column in the group of expandable column or not expanded AND
                ...(hasExpandableHeader &&
                    columns.length - 1 !== columnIndex &&
                    (isHeader || isTotals) &&
                    currentExpandedColumnCount === 0 &&
                    Css_1.Css.boxShadow(`inset -1px -1px 0 ${Css_1.Palette.Gray200}`).$),
                // Or level-specific styling
                ...(!isHeader && !isTotals && !isExpandableHeader && !!style.levels && ((_b = style.levels[level]) === null || _b === void 0 ? void 0 : _b.cellCss)),
                // Level specific styling for the first content column
                ...(applyFirstContentColumnStyles && !!style.levels && ((_c = style.levels[level]) === null || _c === void 0 ? void 0 : _c.firstContentColumn)),
                // The specific cell's css (if any from GridCellContent)
                ...rowStyleCellCss,
                // Apply active row styling for non-nested card styles.
                ...(isActive ? Css_1.Css.bgColor((_d = style.activeBgColor) !== null && _d !== void 0 ? _d : Css_1.Palette.Blue50).$ : {}),
                // Add any cell specific style overrides
                ...((0, utils_1.isGridCellContent)(maybeContent) && maybeContent.typeScale ? Css_1.Css[maybeContent.typeScale].$ : {}),
                // And any cell specific css
                ...((0, utils_1.isGridCellContent)(maybeContent) && maybeContent.css ? maybeContent.css : {}),
                // Apply cell highlight styles to active cell and hover
                ...Css_1.Css.if(applyCellHighlight && isCellActive).br4.boxShadow(`inset 0 0 0 1px ${Css_1.Palette.Blue700}`).$,
                // Define the width of the column on each cell. Supports col spans.
                // If we have a 'levelIndent' defined, then subtract that amount from the first content column's width to ensure all columns will still line up properly
                width: `calc(${columnSizes.slice(columnIndex, columnIndex + currentColspan).join(" + ")}${applyFirstContentColumnStyles && levelIndent ? ` - ${levelIndent}px` : ""})`,
            };
            const cellClassNames = revealOnRowHover ? revealOnRowHoverClass : undefined;
            const cellOnClick = applyCellHighlight ? () => api.setActiveCellId(cellId) : undefined;
            const tooltip = (0, utils_1.isGridCellContent)(maybeContent) ? maybeContent.tooltip : undefined;
            const renderFn = ((rowStyle === null || rowStyle === void 0 ? void 0 : rowStyle.renderCell) || (rowStyle === null || rowStyle === void 0 ? void 0 : rowStyle.rowLink)) && wrapAction
                ? (0, cell_1.rowLinkRenderFn)(as, currentColspan)
                : isHeader || isTotals || isExpandableHeader
                    ? (0, cell_1.headerRenderFn)(column, as, currentColspan)
                    : (rowStyle === null || rowStyle === void 0 ? void 0 : rowStyle.onClick) && wrapAction
                        ? (0, cell_1.rowClickRenderFn)(as, api, currentColspan)
                        : (0, cell_1.defaultRenderFn)(as, currentColspan);
            return renderFn(columnIndex, cellCss, content, row, rowStyle, cellClassNames, cellOnClick, tooltip);
        })) }));
    return row.draggable ? ((0, jsx_runtime_1.jsx)("div", { css: containerCss, 
        // these events are necessary to get the dragged-over row for the drop event
        // and spacer styling
        onDrop: (evt) => onDrop === null || onDrop === void 0 ? void 0 : onDrop(row, evt), onDragEnter: (evt) => onDragEnter === null || onDragEnter === void 0 ? void 0 : onDragEnter(row, evt), onDragOver: (evt) => {
            // when the event isn't called due to debounce, we still need to
            // call preventDefault for the drop event to fire
            evt.preventDefault();
            onDragOverDebounced(row, evt);
        }, children: RowContent() })) : ((0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: RowContent() }));
}
/**
 * Memoizes rows so that re-rendering the table doesn't re-render every single row.
 *
 * We used to have a custom `propsAreEqual` method that would only compare `props.rows`
 * on the `data` attribute, b/c our `createRows` methods do not create stable row identities;
 * i.e. if one row changes, they all change.
 *
 * However, now RowState.row synthesizes a `row` that only reactively changes when
 * that row's `data` explicitly changes, so we no longer need a custom `propsAreEqual` here.
 */
// Declared as a const + `as typeof RowImpl` to work with generics, see https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087#issuecomment-656596623
exports.Row = (0, mobx_react_1.observer)(RowImpl);
